import GoogleChart from "./GoogleChart";
import { SOURCE_ORDER, SOURCE_META, formatBucketLabel, alignSeriesForOverlay } from "../utils";

// Revisual's own source colors (matches SOURCE_CONFIG used on the dashboard widget),
// rather than an arbitrary approximation of the Figma palette.
const SOURCE_COLORS = {
  widget: "#378ADD",
  direct: "#888780",
  qr: "#1D9E75",
  email: "#D85A30",
};
const EVENT_LINE_COLOR = "#B146B3"; // brand 600 — selection is a different concern than source, kept distinct
const DIMMED_OPACITY = 0.35;

const baseOptions = {
  fontName: "Inter",
  chartArea: { left: 56, top: 28, right: 16, bottom: 56 },
  legend: { position: "top", alignment: "end", textStyle: { fontSize: 12, color: "#535862" } },
  hAxis: {
    textStyle: { fontSize: 12, color: "#717680" },
    gridlines: { color: "transparent" },
    slantedText: true,
    slantedTextAngle: 45,
    maxAlternation: 1,
  },
  vAxis: { textStyle: { fontSize: 12, color: "#717680" }, gridlines: { color: "#F5F5F5" }, minValue: 0 },
  isStacked: true,
  backgroundColor: "transparent",
  // Thinner bars with more breathing room between them.
  bar: { groupWidth: "45%" },
  // Hovering anywhere on a bar shows all four source values together,
  // not just whichever segment is under the cursor.
  focusTarget: "category",
  annotations: {
    alwaysOutside: true,
    textStyle: { fontSize: 11, color: "#414651", bold: true },
    stem: { color: "transparent" },
  },
};

// Google Charts' classic ColumnChart/ComboChart renderer (SVG via GViz) has no
// option for rounded bar corners — `cornerRadius` etc. simply aren't part of its
// API. If rounded tops matter enough visually, the practical options are:
//  (a) drop the react-google-charts dependency for this specific chart in favor
//      of a hand-rolled SVG/Recharts bar chart, or
//  (b) a CSS-only hack clipping each bar group with a rounded mask, which is
//      fragile since Google Charts re-renders its own SVG on every resize/update.
// Given the reliability requirement, this is left square for now — flag if (a)
// is worth the tradeoff.

const buildAggregateRows = (series, dimmed = false) =>
  series.map((bucket) => {
    const row = [bucket.bucket_start_label];
    let total = 0;
    SOURCE_ORDER.forEach((key, idx) => {
      const value = Number(bucket.sources?.[key]) || 0;
      total += value;
      row.push(value);
      // per-row style role controls opacity only (dashed/lighter for partial
      // buckets) — color comes from the `colors` chart option below, so it
      // always matches the legend swatches exactly.
      const opacity = dimmed ? DIMMED_OPACITY : bucket.is_partial ? 0.6 : 1;
      row.push(`fill-opacity: ${opacity};`);
      if (idx === SOURCE_ORDER.length - 1) {
        // Annotation on the topmost stacked series renders at the top of
        // the whole bar — the only way to surface a visible running total
        // on a stacked column chart without a separate overlay series.
        row.push(total > 0 ? String(total) : null);
      }
    });
    return row;
  });

const aggregateColumns = () => {
  const cols = ["Bucket"];
  SOURCE_ORDER.forEach((key, idx) => {
    cols.push(SOURCE_META[key].label);
    cols.push({ type: "string", role: "style" });
    if (idx === SOURCE_ORDER.length - 1) {
      cols.push({ type: "string", role: "annotation" });
    }
  });
  return cols;
};

const AggregateChart = ({ series, resolution, dimmed, height = 260 }) => {
  const labeled = series.map((b) => ({ ...b, bucket_start_label: formatBucketLabel(b.bucket_start, resolution) }));
  const data = [aggregateColumns(), ...buildAggregateRows(labeled, dimmed)];
  const colors = SOURCE_ORDER.map((key) => SOURCE_COLORS[key]);

  return (
    <GoogleChart
      chartType="ColumnChart"
      data={data}
      options={{ ...baseOptions, colors }}
      height={`${height}px`}
    />
  );
};

// Overlay mode: aggregate stacked bars (dimmed) + selected event's total as a line, combo chart.
const OverlayChart = ({ series, selectedEvent, resolution, height = 260 }) => {
  const aligned = alignSeriesForOverlay(series, selectedEvent?.series || []);
  const columns = ["Bucket", "All events", { type: "string", role: "style" }, selectedEvent?.name || "Selected event"];
  const rows = aligned.map((b) => [
    formatBucketLabel(b.bucket_start, resolution),
    b.aggregateTotal,
    `fill-color: #A4A7AE; fill-opacity: ${b.is_partial ? 0.25 : DIMMED_OPACITY};`,
    b.eventTotal,
  ]);

  return (
    <GoogleChart
      chartType="ComboChart"
      data={[columns, ...rows]}
      options={{
        ...baseOptions,
        isStacked: false,
        seriesType: "bars",
        series: { 1: { type: "line", color: EVENT_LINE_COLOR, lineWidth: 3, pointSize: 5 } },
      }}
      height={`${height}px`}
    />
  );
};

const FocusChart = ({ series, selectedEvent, resolution }) => (
  <div className="ea-chart__focus-stack">
    <div className="ea-chart__focus-panel">
      <p className="ea-chart__focus-label">All events</p>
      <AggregateChart series={series} resolution={resolution} height={140} />
    </div>
    <div className="ea-chart__focus-panel">
      <p className="ea-chart__focus-label">{selectedEvent?.name || "Selected event"} (own scale)</p>
      <AggregateChart series={selectedEvent?.series || []} resolution={resolution} height={140} />
    </div>
  </div>
);

/**
 * mode:
 *  - 'aggregate' (default, no selection)
 *  - 'overlay'   (event selected, focusView=false)
 *  - 'focus'     (event selected, focusView=true)
 */
const UsageChart = ({ series, selectedEvent, resolution, focusView }) => {
  if (!series || series.length === 0) return null;

  if (!selectedEvent) {
    return <AggregateChart series={series} resolution={resolution} />;
  }
  if (focusView) {
    return <FocusChart series={series} selectedEvent={selectedEvent} resolution={resolution} />;
  }
  return <OverlayChart series={series} selectedEvent={selectedEvent} resolution={resolution} />;
};

export default UsageChart;
